import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Eye, CheckCircle2, Target, Zap } from 'lucide-react';

const DefensivePreventive = () => {
  // Spring plus nerveux pour éviter l'effet de lourdeur
  const cardSpring = { 
    type: "spring", 
    stiffness: 120, // Plus de tension
    damping: 20,    // Moins de rebond lent
    mass: 1 
  };

  const content = {
    defensive: {
      title: "OSINT défensif",
      icon: <ShieldCheck size={26} strokeWidth={1.5} />,
      description: "Identifier les informations publiques exposées afin de réduire les risques de cyberattaque. Nous analysons ce que les attaquants voient de vous pour fermer les portes avant qu'ils ne les franchissent.",
      features: [
        "Cartographie de la surface d'attaque",
        "Détection de fuites de données confidentielles",
        "Nettoyage de l'empreinte numérique"
      ]
    },
    preventive: {
      title: "OSINT préventif",
      icon: <Eye size={26} strokeWidth={1.5} />,
      description: "Surveiller l'environnement numérique pour anticiper les menaces et protéger l'organisation. Une veille stratégique continue pour détecter les signaux faibles d'une attaque en préparation.",
      features: [
        "Veille stratégique sur le Dark Web",
        "Surveillance des forums spécialisés",
        "Anticipation des campagnes de phishing"
      ]
    }
  };

  return (
    <section className="w-full bg-white py-20 px-6 flex flex-col items-center overflow-hidden">
      
      {/* Header Section */}
      <div className="text-center mb-12 max-w-3xl">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ color: '#3b82f6', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}
          className="mb-4"
        >
          Cybersécurité Avancée
        </motion.p>

        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.04em', lineHeight: 1.1 }}
          className="mb-6"
        >
          OSINT <span className="text-[#3b82f6]">défensif et préventif</span>
        </motion.h2>

        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-slate-500 text-base font-medium max-w-2xl mx-auto leading-relaxed"
        >
          Maîtrisez votre empreinte numérique pour une protection proactive face aux menaces émergentes.
        </motion.p>
      </div>

      {/* Main Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-[1100px] w-full mb-8">
        
        {/* Defensive Card - Slides from Left */}
        <motion.div
          initial={{ opacity: 0, x: -100, rotate: -2 }}
          whileInView={{ opacity: 1, x: 0, rotate: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={cardSpring}
          whileHover={{ y: -8, transition: { duration: 0.2 } }}
          className="group bg-[#f8fafc] p-10 rounded-[24px] border border-slate-100 flex flex-col transition-all duration-300"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-8 text-blue-600 shadow-sm transition-all duration-500 group-hover:bg-blue-600 group-hover:text-white group-hover:rotate-[360deg] group-hover:shadow-blue-200"
          >
            {content.defensive.icon}
          </motion.div>
          
          <h3 className="text-xl font-extrabold text-[#0f172a] mb-5">{content.defensive.title}</h3>
          
          <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8 flex-grow">
            {content.defensive.description}
          </p>
          
          <div className="space-y-4">
            {content.defensive.features.map((f, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + (i * 0.1) }}
                className="flex items-center gap-3"
              >
                <CheckCircle2 size={18} className="text-blue-500 flex-shrink-0" />
                <span className="text-slate-700 font-bold text-xs">{f}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Preventive Card - Slides from Right */}
        <motion.div
          initial={{ opacity: 0, x: 100, rotate: 2 }}
          whileInView={{ opacity: 1, x: 0, rotate: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={cardSpring}
          whileHover={{ y: -8, transition: { duration: 0.2 } }}
          className="group bg-[#f8fafc] p-10 rounded-[24px] border border-slate-100 flex flex-col transition-all duration-300"
        >
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-8 text-blue-600 shadow-sm transition-all duration-500 group-hover:bg-blue-600 group-hover:text-white group-hover:rotate-[-360deg] group-hover:shadow-blue-200"
          >
            {content.preventive.icon}
          </motion.div>
          
          <h3 className="text-xl font-extrabold text-[#0f172a] mb-5">{content.preventive.title}</h3>
          
          <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8 flex-grow">
            {content.preventive.description}
          </p>
          
          <div className="space-y-4">
            {content.preventive.features.map((f, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + (i * 0.1) }}
                className="flex items-center gap-3"
              >
                <CheckCircle2 size={18} className="text-blue-500 flex-shrink-0" />
                <span className="text-slate-700 font-bold text-xs">{f}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Summary Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[1100px] w-full">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, type: "spring", stiffness: 100 }}
          className="bg-[#f8fafc] py-5 px-8 rounded-2xl border border-slate-100 flex items-center gap-5 group hover:bg-white transition-all shadow-sm"
        >
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm">
            <Target size={20} />
          </div>
          <div>
            <h4 className="font-extrabold text-[#0f172a] text-sm">Réduction des risques</h4>
            <p className="text-slate-500 text-[11px] font-medium">Analyse approfondie de la surface d'attaque externe.</p>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7, type: "spring", stiffness: 100 }}
          className="bg-[#f8fafc] py-5 px-8 rounded-2xl border border-slate-100 flex items-center gap-5 group hover:bg-white transition-all shadow-sm"
        >
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300 shadow-sm">
            <Zap size={20} />
          </div>
          <div>
            <h4 className="font-extrabold text-[#0f172a] text-sm">Anticipation des menaces</h4>
            <p className="text-slate-500 text-[11px] font-medium">Veille constante sur les vecteurs d'attaque potentiels.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DefensivePreventive;